import { useEffect, useState } from 'react';
import classNames from 'classnames';
import ColorOptions from 'helpers/types';
import { UI_Button } from 'ui/components/1-atoms/Action/Button/Button';
import { UI_Link, UI_LinkProps } from 'ui/components/1-atoms/Action/Link/Link';
import { UI_LinkButton } from 'ui/components/1-atoms/Action/LinkButton/LinkButton';
import { UI_Heading } from 'ui/components/1-atoms/Heading/Heading';
import { UI_Kicker, UI_KickerProps } from 'ui/components/1-atoms/Kicker/Kicker';
import { UI_Icon } from 'ui/components/1-atoms/Media/Icon/Icon';
import { UI_Picture, UI_PictureProps } from 'ui/components/1-atoms/Media/Picture/Picture';
import { UI_Container } from 'ui/components/4-habitats/Container/Container';
import { UI_TopLevelGrid } from 'ui/components/4-habitats/TopLevelGrid/TopLevelGrid';
import { UI_TopLevelGridCell } from 'ui/components/4-habitats/TopLevelGrid/TopLevelGridCell';
import LeftButtonArrow from 'ui/icons/icon-left-button-arrow.svg';
import RightButtonArrow from 'ui/icons/icon-right-button-arrow.svg';
import styles from './CaseSliderItem.module.scss';

export interface UI_CaseSliderItemProps {
	heading: string;
	bgColor: ColorOptions;
	kicker: UI_KickerProps;
	seeAllLink?: UI_LinkProps;
	seeAllLinkLabel?: string;
	caseLink: UI_LinkProps;
	image?: UI_PictureProps;
	slideNumber?: number;
	onHandlePrevClick?: () => void;
	onHandleNextClick?: () => void;
	navigationPrevLabel?: string;
	navigationNextLabel?: string;
	globalTheme?: string;
	disablePrevButton?: boolean;
	disableNextButton?: boolean;
}

export const UI_CaseSliderItem = ({
	heading,
	bgColor,
	kicker,
	seeAllLink,
	seeAllLinkLabel,
	caseLink,
	image,
	slideNumber,
	onHandlePrevClick,
	onHandleNextClick,
	navigationPrevLabel,
	navigationNextLabel,
	globalTheme,
	disablePrevButton,
	disableNextButton,
}: UI_CaseSliderItemProps) => {
	// The case link sits next to the heading on desktop and below the image on mobile,
	// so the breakpoint has to be mirrored in JS. Resolved after mount to keep SSR markup stable.
	const [mobileLayout, setMobileLayout] = useState(false);

	useEffect(() => {
		// Matches the "md" breakpoint used in CaseSliderItem.module.scss.
		const handleResize = () => setMobileLayout(window.innerWidth < 768);

		handleResize();
		window.addEventListener('resize', handleResize);

		return () => window.removeEventListener('resize', handleResize);
	}, []);

	const caseLinkButton = caseLink?.title && (
		<UI_LinkButton style="secondary" inheritsColor={true} className={styles.CaseSliderItem_button} target={caseLink.target} href={caseLink.href}>
			{caseLink.title}
		</UI_LinkButton>
	);

	return (
		<div className={classNames(styles.CaseSliderItem, styles[`u-color___${bgColor}`])}>
			<UI_Container width="Standard">
				<div className={styles.CaseSliderItem_wrapper}>
					{kicker?.firstWord && kicker?.secondWord && <UI_Kicker className={styles.CaseSlider_kicker} {...kicker} />}

					{seeAllLink?.title && (
						<UI_Link
							className={classNames(styles.CaseSlider_link, {
								[styles.CaseSlider_link___noKicker]: !kicker?.firstWord && !kicker?.secondWord,
							})}
							{...seeAllLink}
							iconLink={true}
							title={seeAllLinkLabel}
							globalTheme={globalTheme}
						>
							{seeAllLink.title}
						</UI_Link>
					)}

					{slideNumber > 1 && (
						<div className={styles.CaseSliderItem_navigation}>
							{globalTheme === 'kogp' ? (
								<>
									<UI_Button
										onClick={onHandlePrevClick}
										style="noAnimation"
										ariaLabel={navigationPrevLabel || 'Previous slide'}
										disabled={disablePrevButton}
									>
										<UI_Icon size="md">
											<LeftButtonArrow />
										</UI_Icon>
									</UI_Button>
									<UI_Button
										onClick={onHandleNextClick}
										style="noAnimation"
										ariaLabel={navigationNextLabel || 'Next slide'}
										disabled={disableNextButton}
									>
										<UI_Icon size="md">
											<RightButtonArrow />
										</UI_Icon>
									</UI_Button>
								</>
							) : (
								<>
									<UI_Button
										style="arrowPrev"
										ariaLabel={navigationPrevLabel || 'Previous slide'}
										onClick={onHandlePrevClick}
										disabled={disablePrevButton}
									>
										{''}
									</UI_Button>
									<UI_Button
										style="arrowNext"
										ariaLabel={navigationNextLabel || 'Next slide'}
										onClick={onHandleNextClick}
										disabled={disableNextButton}
									>
										{''}
									</UI_Button>
								</>
							)}
						</div>
					)}

					<div className={styles.CaseSliderItem_content}>
						<UI_TopLevelGrid nogutter>
							<UI_TopLevelGridCell desktopWidth="18col" desktopOffset="0col" mobileWidth="100">
								<div className={styles.CaseSliderItem_heading}>
									{heading && (
										<UI_Heading className={styles.CaseSliderItem_text} style="xl" headingLevel="h2">
											{heading}
										</UI_Heading>
									)}
									{!mobileLayout && caseLinkButton}
								</div>

								<div className={styles.CaseSliderItem_imageWrapper}>
									{image?.url && (
										<div className={styles.CaseSliderItem_image}>
											<UI_Picture
												url={image.url}
												altText={image.altText}
												focalPoint={image.focalPoint}
												imageCropping="cover"
												sizes="(max-width: 768px) 100vw, (max-width: 1728px) 66vw, 1150px"
												fileType={image.fileType}
											/>
										</div>
									)}

									{mobileLayout && caseLinkButton}
								</div>
							</UI_TopLevelGridCell>
						</UI_TopLevelGrid>
					</div>
				</div>
			</UI_Container>
		</div>
	);
};
